import { hideLoader, showLoader } from "../Slices/loadingSlice";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { GlobalService, HttpService, UserCountsResponse, parseApiErrors } from "../../modules";
import { UpdateUserProfile } from "./AuthAction";
import { getLanguageHeaders } from "../../utils/languageHeaders";
import i18n from "../../i18n";
export const getUserCategories = createAsyncThunk<any, any>(
    "admin/getUserCategories",
    async ({ page = 1, limit = 10, typeId, filters, isShowLoader = true }, { dispatch, rejectWithValue }) => {
        try {
            if (isShowLoader) {
                dispatch(showLoader());
            }

            let url = "api/v1/categories";
            const params = new URLSearchParams();
            
            // Add pagination parameters
            params.append("page", page.toString());
            params.append("limit", limit.toString());
            
            // Add filters as JSON object if provided
            if (filters && Object.keys(filters).length > 0) {
                params.append("filters", JSON.stringify(filters));
            }
            
            // If typeId is provided, use the type-specific endpoint
            if (typeId) {
                url = `api/v1/categories/type/${typeId}`;
                // Still add pagination and other filters
                if (params.toString()) {
                    url += `?${params.toString()}`;
                }
            } else {
                // Use the main categories endpoint with filters
                if (params.toString()) {
                    url += `?${params.toString()}`;
                }
            }

            const response = await HttpService.get(url, true, getLanguageHeaders());
            
            console.log("API Response Debug:", response);
            
            if (isShowLoader) {
                dispatch(hideLoader());
            }

            if (typeof response == "string" && response.length == 0) {
                return null;
            } else {
                return response;
            }

            // GlobalService.showToast("success", "Success", "User Preferences Updated Successfully");
        } catch (error: any) {
            dispatch(hideLoader());
            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "User Categories failed";

            console.log(error);
            return rejectWithValue(errorMessage);
        }
    }
);
export const createUserCategories = createAsyncThunk<any, { data: any }>(
    "admin/createUserCategories",
    async ({ data }, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());

            const response = await HttpService.post(
                "api/v1/categories",
                data,
                { "Content-Type": "application/json" },
                true
            );

            dispatch(hideLoader());
            GlobalService.showToast("success", i18n.t("toast.titles.success"), i18n.t("toast.categories.created"));
            return response;
        } catch (error: any) {
            dispatch(hideLoader());
            const errorMessage = parseApiErrors(error);
            
            console.log("API Error:", error);
            GlobalService.showToast("error", i18n.t("toast.titles.error"), errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);
export const updateUserCategories = createAsyncThunk<any, { id: string; data: any }>(
    "admin/updateUserCategories",
    async ({ id, data }, { dispatch, rejectWithValue }) => {
        try {
            console.log(data, id);

            dispatch(showLoader());
            const response = await HttpService.patch(
                `api/v1/categories/${id}`,
                data,
                { "Content-Type": "application/json" },
                true
            );

            dispatch(hideLoader());

            GlobalService.showToast("success", i18n.t("toast.titles.success"), i18n.t("toast.categories.updated"));
            return response;
        } catch (error: any) {
            dispatch(hideLoader());
            const errorMessage = parseApiErrors(error);
            
            console.log("API Error:", error);
            GlobalService.showToast("error", i18n.t("toast.titles.error"), errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);
export const deleteUserCategories = createAsyncThunk<any, { id: string }>(
    "admin/deleteUserCategories",
    async ({ id }, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());

            const response = await HttpService.deleteRecord(
                `api/v1/categories/${id}`,
                { "Content-Type": "application/json" },
                true
            );
            dispatch(hideLoader());

            GlobalService.showToast("success", i18n.t("toast.titles.success"), i18n.t("toast.categories.deleted"));
            return { id };
        } catch (error: any) {
            dispatch(hideLoader());
            const errorMessage = parseApiErrors(error);
            
            console.log("API Error:", error);
            GlobalService.showToast("error", i18n.t("toast.titles.error"), errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);

// CSV Bulk Import Types and Functions (following the same pattern as LocationActions)
export interface BulkImportCategoriesResult {
    success: number;
    failed: number;
    created: number;
    updated: number;
    errors: Array<{
        categoryName: string;
        error: string;
    }>;
}

export const bulkImportCategories = async (file: File): Promise<BulkImportCategoriesResult> => {
    const formData = new FormData();
    formData.append("file", file);
    
    // Don't set Content-Type header for FormData - browser will set it automatically with boundary
    const response = await HttpService.post("api/v1/categories/bulk-import", formData, {}, true);
    return response;
};

export const downloadCategoryTemplate = async (): Promise<void> => {
    const blob = await HttpService.getBlob(
        "api/v1/categories/csv/template",
        true,
        {}
    );

    // Create download link
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute("download", "category_import_template.csv");
    link.style.visibility = "hidden";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};
export const getAllVerifications = createAsyncThunk<any, {
    page?: number;
    limit?: number;
    search?: string;
    requestType?: string;
    status?: string;
    professionalType?: string;
    sortBy?: string;
    sortOrder?: string;
    isShowLoader?: boolean;
}>(
    "admin/getAllVerifications",
    async ({ 
        page = 1, 
        limit = 10, 
        search = "", 
        requestType = "all",
        status = "all",
        professionalType = "all",
        sortBy = "createdAt",
        sortOrder = "desc",
        isShowLoader = true
    }, { dispatch, rejectWithValue }) => {
        try {
            if (isShowLoader) {
                dispatch(showLoader());
            }

            // Prepare query parameters
            const params = new URLSearchParams();
            params.append("page", page.toString());
            params.append("limit", limit.toString());

            // Handle sorting
            if (sortBy && sortOrder) {
                params.append("sortBy", sortBy);
                params.append("sortOrder", sortOrder.toUpperCase());
            }

            // Handle search
            if (search && search.trim()) {
                params.append("keyword", search.trim());
            }

            // Handle request type filter
            if (requestType && requestType !== "all") {
                params.append("requestType", requestType);
            }

            // Handle status filter
            if (status && status !== "all") {
                params.append("status", status);
            }

            // Handle professional type filter
            if (professionalType && professionalType !== "all") {
                params.append("professionalType", professionalType);
            }

            // Use the new admin-requests endpoint for flattened verification requests
            const finalUrl = `api/v1/verifications/admin-requests?${params.toString()}`;
            console.log("Verifications API URL:", finalUrl);
            console.log("Sorting parameters:", { sortBy, sortOrder });
            
            const response = await HttpService.get(finalUrl, true, {});

            if (isShowLoader) {
                dispatch(hideLoader());
            }
            return response;
        } catch (error: any) {
            if (isShowLoader) {
                dispatch(hideLoader());
            }
            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "All verification failed";

            console.log(error);
            return rejectWithValue(errorMessage);
        }
    }
);
export const adminupdateVerification = createAsyncThunk<any, { id: string; data: any }>(
    "admin/adminupdateVerification",
    async ({ id, data }, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());
            const response = await HttpService.patch(
                `api/v1/verifications/${id}`,
                data,
                { "Content-Type": "application/json" },
                true
            );

            dispatch(hideLoader());
            console.log(response);

            GlobalService.showToast("success", i18n.t("toast.titles.success"), i18n.t("toast.verifications.updated"));
            return response;
        } catch (error: any) {
            dispatch(hideLoader());
            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "User Update Verifications failed";

            console.log(error);
            return rejectWithValue(errorMessage);
        }
    }
);

export const getAllUsers = createAsyncThunk<any, { 
    page: number; 
    limit: number; 
    roles?: any[];
    search?: string;
    status?: number;
    sortBy?: string;
    sortOrder?: string;
}>(
    "admin/getAllUsers",
    async ({ page, limit, roles, search, status, sortBy = "createdAt", sortOrder = "desc" }, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());

            // Prepare query parameters
            const params = new URLSearchParams();
            params.append("page", page.toString());
            params.append("limit", limit.toString());

            // Handle sorting with the new format
            if (sortBy && sortOrder) {
                const sortArray = [{
                    orderBy: sortBy,
                    order: sortOrder.toUpperCase()
                }];
                params.append("sort", JSON.stringify(sortArray));
            }

            if (search && search.trim()) {
                params.append("keyword", search.trim());
            }

            if (status && status !== 0) {
                params.append("status", status.toString());
            }

            if (roles && roles.length > 0) {
                // Create filters object with roles array
                const filters = {
                    roles: roles.map(role => ({ id: role.id })),
                };
                // Stringify and encode the filters object
                params.append("filters", JSON.stringify(filters));
            }

            const response = await HttpService.get(`api/v1/users?${params.toString()}`, true, {});

            dispatch(hideLoader());
            console.log(response);

            if (!response?.data) {
                return {
                    data: [],
                    totalCount: 0,
                    hasNextPage: false,
                };
            }

            return {
                data: response.data,
                totalCount: response.totalCount || 0,
                hasNextPage: response.hasNextPage || false,
            };
        } catch (error: any) {
            dispatch(hideLoader());
            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "User fetching failed";
            return rejectWithValue(errorMessage);
        }
    }
);

export const getLocalProfessionals = createAsyncThunk<any, { 
    page?: number; 
    limit?: number; 
    search?: string;
    jobUserId?: string;
    category?: string;
    categoryId?: string;
    serviceAreaId?: string;
    minHourlyRate?: number;
    maxHourlyRate?: number;
    isEmergency?: boolean;
    province?: string;
    municipality?: string;
    availability?: string;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
    isShowLoader?: boolean;
}>(
    "admin/getLocalProfessionals",
    async ({ page = 1, limit = 10, search, category, categoryId, serviceAreaId, minHourlyRate, maxHourlyRate, isEmergency, province, municipality, availability, sortBy, sortOrder, isShowLoader = true, jobUserId }, { dispatch, rejectWithValue }) => {
        try {
            if (isShowLoader) {
                dispatch(showLoader());
            }

            // Prepare query parameters
            const params = new URLSearchParams();
            params.append("page", page.toString());
            params.append("limit", limit.toString());

            if (search && search.trim()) {
                params.append("keyword", search.trim());
            }

            if (category && category !== "all") {
                params.append("category", category);
            }

            if (categoryId && categoryId.trim()) {
                params.append("categoryId", categoryId.trim());
            }

            if (serviceAreaId && serviceAreaId.trim()) {
                params.append("serviceAreaId", serviceAreaId.trim());
            }

            if (minHourlyRate !== undefined && minHourlyRate > 0) {
                params.append("minHourlyRate", minHourlyRate.toString());
            }

            if (maxHourlyRate !== undefined && maxHourlyRate > 0) {
                params.append("maxHourlyRate", maxHourlyRate.toString());
            }

            if (isEmergency !== undefined) {
                params.append("isEmergency", isEmergency.toString());
            }

            if (province && province.trim()) {
                params.append("province", province.trim());
            }

            if (municipality && municipality.trim()) {
                params.append("municipality", municipality.trim());
            }

            if (availability && availability.trim()) {
                params.append("availability", availability.trim());
            }

            if (sortBy && sortBy.trim()) {
                params.append("sortBy", sortBy.trim());
            }

            if (sortOrder) {
                params.append("sortOrder", sortOrder);
            }

            if(jobUserId && jobUserId.trim()) {
                params.append("jobUserId", jobUserId.trim());
            }

            const response = await HttpService.get(`api/v1/users/local-professionals?${params.toString()}`, true, {});

            if (isShowLoader) {
                dispatch(hideLoader());
            }

            console.log("Local Professionals API Response:", response);
            console.log("API URL called:", `api/v1/users/local-professionals?${params.toString()}`);

            if (!response?.data) {
                return {
                    data: [],
                    totalCount: 0,
                    hasNextPage: false,
                    currentPage: page,
                    totalPages: Math.ceil(0 / limit)
                };
            }

            return {
                data: response.data,
                totalCount: response.totalCount || 0,
                hasNextPage: response.hasNextPage || false,
                currentPage: page,
                totalPages: Math.ceil((response.totalCount || 0) / limit),
                locationFilters: response.locationFilters || {
                    provinces: [],
                    citiesByProvince: {},
                    municipalities: []
                }
            };
        } catch (error: any) {
            if (isShowLoader) {
                dispatch(hideLoader());
            }
            const errorData = error?.data?.errors;
            const errorMessage = errorData?.message || "Failed to fetch local professionals";
            console.log("Local Professionals API Error:", error);
            return rejectWithValue(errorMessage);
        }
    }
);

export const getInternationalProfessionals = createAsyncThunk<any, { 
    page?: number; 
    limit?: number; 
    search?: string;
    category?: string;
    categoryId?: string;
    location?: string;
    countryId?: string;
    minHourlyRate?: number;
    maxHourlyRate?: number;
    isEmergency?: boolean;
    languages?: string;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
    isShowLoader?: boolean;
    jobUserId?: string;
}>(
    "admin/getInternationalProfessionals",
    async ({ page = 1, limit = 10, search, category, categoryId, location, countryId, minHourlyRate, maxHourlyRate, isEmergency, languages, sortBy, sortOrder, isShowLoader = true, jobUserId }, { dispatch, rejectWithValue }) => {
        try {
            if (isShowLoader) {
                dispatch(showLoader());
            }

            // Prepare query parameters
            const params = new URLSearchParams();
            params.append("page", page.toString());
            params.append("limit", limit.toString());

            if (search && search.trim()) {
                params.append("keyword", search.trim());
            }

            if (category && category !== "all") {
                params.append("category", category);
            }

            if (categoryId && categoryId.trim()) {
                params.append("categoryId", categoryId.trim());
            }

            if (location && location.trim()) {
                params.append("location", location.trim());
            }

            if (countryId && countryId.trim()) {
                params.append("countryId", countryId.trim());
            }

            if (minHourlyRate !== undefined && minHourlyRate > 0) {
                params.append("minHourlyRate", minHourlyRate.toString());
            }

            if (maxHourlyRate !== undefined && maxHourlyRate > 0) {
                params.append("maxHourlyRate", maxHourlyRate.toString());
            }

            if (isEmergency !== undefined) {
                params.append("isEmergency", isEmergency.toString());
            }

            if (languages && languages.trim()) {
                params.append("languages", languages.trim());
            }

            if (sortBy && sortBy.trim()) {
                params.append("sortBy", sortBy.trim());
            }

            if (sortOrder) {
                params.append("sortOrder", sortOrder);
            }

            if(jobUserId && jobUserId.trim()) {
                params.append("jobUserId", jobUserId.trim());
            }

            const response = await HttpService.get(`api/v1/users/international-professionals?${params.toString()}`, true, {});

            if (isShowLoader) {
                dispatch(hideLoader());
            }

            console.log("International Professionals API Response:", response);
            console.log("API URL called:", `api/v1/users/international-professionals?${params.toString()}`);

            if (!response?.data) {
                return {
                    data: [],
                    totalCount: 0,
                    hasNextPage: false,
                    currentPage: page,
                    totalPages: Math.ceil(0 / limit)
                };
            }

            return {
                data: response.data,
                totalCount: response.totalCount || 0,
                hasNextPage: response.hasNextPage || false,
                currentPage: page,
                totalPages: Math.ceil((response.totalCount || 0) / limit)
            };
        } catch (error: any) {
            if (isShowLoader) {
                dispatch(hideLoader());
            }
            const errorData = error?.data?.errors;
            const errorMessage = errorData?.message || "Failed to fetch international professionals";
            console.log("International Professionals API Error:", error);
            return rejectWithValue(errorMessage);
        }
    }
);

export const getEmergencyProfessionals = createAsyncThunk<any, {
    page?: number;
    limit?: number;
    keyword?: string;
    serviceAreaId?: string;
    categoryId?: string;
    sortBy?: string;
    sortOrder?: string;
    isShowLoader?: boolean;
}>(
    "admin/getEmergencyProfessionals",
    async (
        {
            page = 1,
            limit = 10,
            keyword = "",
            serviceAreaId,
            categoryId,
            sortBy = "createdAt",
            sortOrder = "desc",
            isShowLoader = true,
        },
        { dispatch, rejectWithValue }
    ) => {
        try {
            if (isShowLoader) {
                dispatch(showLoader());
            }

            const params = new URLSearchParams();
            params.append("page", page.toString());
            params.append("limit", limit.toString());
            if (keyword && keyword.trim()) {
                params.append("keyword", keyword.trim());
            }
            if (serviceAreaId && serviceAreaId.trim()) {
                params.append("serviceAreaId", serviceAreaId.trim());
            }
            if (categoryId && categoryId.trim()) {
                params.append("categoryId", categoryId.trim());
            }
            if (sortBy && sortOrder) {
                params.append("sortBy", sortBy);
                params.append("sortOrder", sortOrder);
            }

            const response = await HttpService.get(
                `api/v1/users/emergency-professionals?${params.toString()}`,
                true,
                {}
            );

            if (isShowLoader) {
                dispatch(hideLoader());
            }

            if (!response?.data) {
                return {
                    data: [],
                    totalCount: 0,
                    hasNextPage: false,
                    currentPage: page,
                    totalPages: Math.ceil(0 / limit),
                };
            }

            return {
                data: response.data,
                totalCount: response.totalCount || 0,
                hasNextPage: response.hasNextPage || false,
                currentPage: page,
                totalPages: Math.ceil((response.totalCount || 0) / limit),
            };
        } catch (error: any) {
            if (isShowLoader) {
                dispatch(hideLoader());
            }
            const errorData = error?.data?.errors;
            const errorMessage = errorData?.message || "Failed to fetch emergency professionals";
            return rejectWithValue(errorMessage);
        }
    }
);

export const getAllUserCounts = createAsyncThunk<UserCountsResponse | null>(
    "admin/getAllUserCounts",
    async (_, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());
            const response = await HttpService.get("api/v1/users/counts", true, {});
            dispatch(hideLoader());

            if (typeof response === "string" && response.length === 0) {
                return null;
            } else {
                return response;
            }
        } catch (error: any) {
            dispatch(hideLoader());
            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "User Counts fetching failed";
            return rejectWithValue(errorMessage);
        }
    }
);
export const createSupportTicketNotes = createAsyncThunk<any, { data: any }>(
    "admin/createSupportTicketNotes",
    async ({ data }, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());

            const response = await HttpService.post(
                "api/v1/support-ticket-notes",
                data,
                { "Content-Type": "application/json" },
                true
            );
            dispatch(hideLoader());
            GlobalService.showToast("success", i18n.t("toast.titles.success"), i18n.t("toast.tickets.notesCreated"));
            if (typeof response == "string" && response.length == 0) {
                return null;
            } else {
                return response;
            }
        } catch (error: any) {
            dispatch(hideLoader());
            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "User created Notes failed";

            console.log(error);
            return rejectWithValue(errorMessage);
        }
    }
);
export const getUserSupportTicketNotes = createAsyncThunk<any, any>(
    "admin/getUserSupportTicketNotes",
    async ({ id: ticketId, isShowLoader = true }, { dispatch, rejectWithValue }) => {
        try {
            if (isShowLoader) {
                dispatch(showLoader());
            }
            console.log(ticketId, "sending id");

            const response = await HttpService.get(
                `api/v1/support-ticket-notes/notes/${ticketId}`,

                true,
                {}
            );
            if (isShowLoader) {
                dispatch(hideLoader());
            }
            if (typeof response == "string" && response.length == 0) {
                return [];
            } else {
                return response;
            }
        } catch (error: any) {
            dispatch(hideLoader());

            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "User Notes  failed";

            console.log(error);
            return rejectWithValue(errorMessage);
        }
    }
);

// Countries Actions

// Countries Actions
export const getAllCountries = createAsyncThunk<any, { 
    page?: number; 
    limit?: number; 
    search?: string;
    isShowLoader?: boolean;
    append?: boolean;
}>(
    "admin/getAllCountries",
    async ({ page = 1, limit = 10, search = "", isShowLoader = true, append = false }, { dispatch, rejectWithValue }) => {
        try {
            if (isShowLoader) {
                dispatch(showLoader());
            }

            // Build query parameters
            const params = new URLSearchParams();
            params.append("page", page.toString());
            params.append("limit", limit.toString());

            if (search && search.trim()) {
                params.append("name", search.trim());
            }

            const response = await HttpService.get(`api/v1/countries?${params.toString()}`, true, {});
            
            if (isShowLoader) {
                dispatch(hideLoader());
            }

            if (typeof response === "string" && response.length === 0) {
                return {
                    data: [],
                    hasNextPage: false,
                    totalCount: 0,
                };
            } else {
                return {
                    data: response.data || response,
                    totalCount: response.totalCount || 0,
                    hasNextPage: response.hasNextPage || false,
                };
            }
        } catch (error: any) {
            dispatch(hideLoader());
            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "Getting Countries failed";

            console.log(error);
            return rejectWithValue(errorMessage);
        }
    }
);

// Get country by ID
export const getCountryById = createAsyncThunk<any, {
    id: string;
    isShowLoader?: boolean;
}>(
    "admin/getCountryById",
    async ({ id, isShowLoader = false }, { dispatch, rejectWithValue }) => {
        try {
            if (isShowLoader) {
                dispatch(showLoader());
            }

            const response = await HttpService.get(`api/v1/countries/${id}`, true, {});
            
            if (isShowLoader) {
                dispatch(hideLoader());
            }

            return response;
        } catch (error: any) {
            if (isShowLoader) {
                dispatch(hideLoader());
            }
            
            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "Getting Country failed";

            console.log(error);
            return rejectWithValue(errorMessage);
        }
    }
);

export const createCountry = createAsyncThunk<any, { data: any }>(
    "admin/createCountry",
    async ({ data }, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());

            const response = await HttpService.post(
                "api/v1/countries",
                data,
                { "Content-Type": "application/json" },
                true
            );

            dispatch(hideLoader());
            GlobalService.showToast("success", i18n.t("toast.titles.success"), i18n.t("toast.country.created"));
            return response;
        } catch (error: any) {
            dispatch(hideLoader());
            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "Country creation failed";

            console.log(error);
            return rejectWithValue(errorMessage);
        }
    }
);

// User Status Management Actions
export const updateUserStatus = createAsyncThunk<any, { userId: string; status: number }>(
    "admin/updateUserStatus",
    async ({ userId, status }, { dispatch, rejectWithValue }) => {
        try {
            const data = { status: { id: status } };
            const result = await dispatch(UpdateUserProfile({ id: userId, data }));
            
            if (UpdateUserProfile.fulfilled.match(result)) {
                const statusText = status === 1 ? "activated" : status === 3 ? "suspended" : "updated";
                GlobalService.showToast("success", i18n.t("toast.titles.success"), i18n.t("toast.userAccount.statusUpdated", { status: statusText }));
                return { userId, status, user: result.payload };
            } else {
                throw new Error("Failed to update user status");
            }
        } catch (error: any) {
            console.log("API Error:", error);
            GlobalService.showToast("error", i18n.t("toast.titles.error"), i18n.t("toast.userAccount.statusUpdateFailed"));
            return rejectWithValue(error.message || "Failed to update user status");
        }
    }
);

export const suspendUser = createAsyncThunk<any, { userId: string }>(
    "admin/suspendUser",
    async ({ userId }, { dispatch, rejectWithValue }) => {
        try {
            return await dispatch(updateUserStatus({ userId, status: 3 })).unwrap();
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const activateUser = createAsyncThunk<any, { userId: string }>(
    "admin/activateUser",
    async ({ userId }, { dispatch, rejectWithValue }) => {
        try {
            return await dispatch(updateUserStatus({ userId, status: 1 })).unwrap();
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const deleteUser = createAsyncThunk<any, { userId: string }>(
    "admin/deleteUser",
    async ({ userId }, { dispatch, rejectWithValue }) => {
        try {
            return await dispatch(updateUserStatus({ userId, status: 4 })).unwrap();
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

// Change Password Action
export const changeUserPassword = createAsyncThunk<any, { data: { currentPassword: string; newPassword: string } }>(
    "admin/changeUserPassword",
    async ({ data }, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());

            const response = await HttpService.post(
                "api/v1/users/change-password",
                data,
                { "Content-Type": "application/json" },
                true
            );

            dispatch(hideLoader());
            GlobalService.showToast("success", i18n.t("toast.titles.success"), i18n.t("toast.userAccount.passwordChanged"));
            return response;
        } catch (error: any) {
            dispatch(hideLoader());
            const errorMessage = parseApiErrors(error);
            
            console.log("API Error:", error);
            GlobalService.showToast("error", i18n.t("toast.titles.error"), errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);

// Admin Dashboard Stats Action
export const getAdminDashboardStats = createAsyncThunk<any, { isShowLoader?: boolean }>(
    "admin/getAdminDashboardStats",
    async ({ isShowLoader = true }, { dispatch, rejectWithValue }) => {
        try {
            if (isShowLoader) {
                dispatch(showLoader());
            }

            const response = await HttpService.get("api/v1/admin-stats/dashboard", true, {});

            if (isShowLoader) {
                dispatch(hideLoader());
            }

            if (typeof response === "string" && response.length === 0) {
                return null;
            } else {
                return response;
            }
        } catch (error: any) {
            if (isShowLoader) {
                dispatch(hideLoader());
            }
            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "Admin dashboard stats fetching failed";

            console.log(error);
            return rejectWithValue(errorMessage);
        }
    }
);

// Admin Revenue Stats Action
export const getAdminRevenueStats = createAsyncThunk<any, { isShowLoader?: boolean; period?: string }>(
    "admin/getAdminRevenueStats",
    async ({ isShowLoader = true, period = "month" }, { dispatch, rejectWithValue }) => {
        try {
            if (isShowLoader) {
                dispatch(showLoader());
            }

            // Build query parameters
            const params = new URLSearchParams();
            if (period) {
                params.append("period", period);
            }

            const url = `api/v1/admin-stats/revenues${params.toString() ? `?${params.toString()}` : ""}`;
            const response = await HttpService.get(url, true, {});

            if (isShowLoader) {
                dispatch(hideLoader());
            }

            if (typeof response === "string" && response.length === 0) {
                return [];
            } else {
                return response;
            }
        } catch (error: any) {
            if (isShowLoader) {
                dispatch(hideLoader());
            }
            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "Admin revenue stats fetching failed";

            console.log(error);
            return rejectWithValue(errorMessage);
        }
    }
);

// Admin Business Analytics Action
export const getAdminBusinessAnalytics = createAsyncThunk<any, { isShowLoader?: boolean; period?: string }>(
    "admin/getAdminBusinessAnalytics",
    async ({ isShowLoader = true, period = "month" }, { dispatch, rejectWithValue }) => {
        try {
            if (isShowLoader) {
                dispatch(showLoader());
            }

            // Build query parameters
            const params = new URLSearchParams();
            if (period) {
                params.append("period", period);
            }

            const url = `api/v1/admin-stats/businesses${params.toString() ? `?${params.toString()}` : ""}`;
            const response = await HttpService.get(url, true, {});

            if (isShowLoader) {
                dispatch(hideLoader());
            }

            if (typeof response === "string" && response.length === 0) {
                return null;
            } else {
                return response;
            }
        } catch (error: any) {
            if (isShowLoader) {
                dispatch(hideLoader());
            }
            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "Admin business analytics fetching failed";

            console.log(error);
            return rejectWithValue(errorMessage);
        }
    }
);

// Admin User Analytics Action
export const getAdminUserAnalytics = createAsyncThunk<any, { isShowLoader?: boolean; period?: string }>(
    "admin/getAdminUserAnalytics",
    async ({ isShowLoader = true, period = "month" }, { dispatch, rejectWithValue }) => {
        try {
            if (isShowLoader) {
                dispatch(showLoader());
            }

            // Build query parameters
            const params = new URLSearchParams();
            if (period) {
                params.append("period", period);
            }

            const url = `api/v1/admin-stats/users${params.toString() ? `?${params.toString()}` : ""}`;
            const response = await HttpService.get(url, true, {});

            if (isShowLoader) {
                dispatch(hideLoader());
            }

            if (typeof response === "string" && response.length === 0) {
                return null;
            } else {
                return response;
            }
        } catch (error: any) {
            if (isShowLoader) {
                dispatch(hideLoader());
            }
            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "Admin user analytics fetching failed";

            console.log(error);
            return rejectWithValue(errorMessage);
        }
    }
);

// Admin Growth Analytics Action
export const getAdminGrowthAnalytics = createAsyncThunk<any, { isShowLoader?: boolean; period?: string }>(
    "admin/getAdminGrowthAnalytics",
    async ({ isShowLoader = true, period = "month" }, { dispatch, rejectWithValue }) => {
        try {
            if (isShowLoader) {
                dispatch(showLoader());
            }

            // Build query parameters
            const params = new URLSearchParams();
            if (period) {
                params.append("period", period);
            }

            const url = `api/v1/admin-stats/growth${params.toString() ? `?${params.toString()}` : ""}`;
            const response = await HttpService.get(url, true, {});

            if (isShowLoader) {
                dispatch(hideLoader());
            }

            if (typeof response === "string" && response.length === 0) {
                return null;
            } else {
                return response;
            }
        } catch (error: any) {
            if (isShowLoader) {
                dispatch(hideLoader());
            }
            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "Admin growth analytics fetching failed";

            console.log(error);
            return rejectWithValue(errorMessage);
        }
    }
);

// Admin Quote Clients Action
export const getQuoteClients = createAsyncThunk<any, { 
    page?: number; 
    limit?: number; 
    search?: string;
    status?: string;
    isShowLoader?: boolean;
}>(
    "admin/getQuoteClients",
    async ({ page = 1, limit = 10, search = "", status = "all", isShowLoader = true }, { dispatch, rejectWithValue }) => {
        try {
            if (isShowLoader) {
                dispatch(showLoader());
            }

            // Build query parameters
            const params = new URLSearchParams();
            params.append("page", page.toString());
            params.append("limit", limit.toString());

            // Add filters for client role (role id 2)
            const filters: any = {
                roles: [{ id: 2 }]
            };

            // Add status filter if not "all"
            if (status && status !== "all") {
                filters.status = parseInt(status);
            }

            params.append("filters", JSON.stringify(filters));

            if (search && search.trim()) {
                params.append("keyword", search.trim());
            }

            const response = await HttpService.get(`api/v1/users/with-quote-job-count?${params.toString()}`, true, {});

            if (isShowLoader) {
                dispatch(hideLoader());
            }

            console.log("Quote Clients API Response:", response);

            if (!response?.data) {
                return {
                    data: [],
                    totalCount: 0,
                    hasNextPage: false,
                    currentPage: page,
                    totalPages: Math.ceil(0 / limit)
                };
            }

            return {
                data: response.data,
                totalCount: response.totalCount || 0,
                hasNextPage: response.hasNextPage || false,
                currentPage: page,
                totalPages: Math.ceil((response.totalCount || 0) / limit)
            };
        } catch (error: any) {
            if (isShowLoader) {
                dispatch(hideLoader());
            }
            const errorData = error?.data?.errors;
            const errorMessage = errorData?.message || "Failed to fetch quote clients";

            console.log("Quote Clients API Error:", error);
            return rejectWithValue(errorMessage);
        }
    }
);
